import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {OnePaneLayoutFullWidthSection} from '@udemy/react-layout-components';
import {Carousel, IndicatorDots} from '@udemy/react-structure-components';
import {BannerClickEvent, BannerImpressionEvent} from '@udemy/smart-bar';

import {BannerData} from 'src/features/lohp/hooks/useFetchHeroBanners';

import {MarketingHeroBannerProps} from '../marketing-hero-banner.react-component';
import {ConsolidatedHeroBannerSlide} from './consolidated-hero-banner-slide.react-component';
import styles from './consolidated-hero-banner.module.less';

const SCROLL_PORT_SELECTOR = '[data-carousel-scroll-port]';

const SHAPE_DARKEN_FACTOR = 0.7;

// TODO: replace with `bannerData.shape_color` when CMP adds the field
// (upcoming Comms sprint).
const deriveShapeColor = (backgroundHex: string | undefined): string | undefined => {
    if (!backgroundHex) return undefined;
    const hex = backgroundHex.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;
    const num = parseInt(hex, 16);
    const r = Math.floor((num >> 16) * SHAPE_DARKEN_FACTOR);
    const g = Math.floor(((num >> 8) & 0xff) * SHAPE_DARKEN_FACTOR);
    const b = Math.floor((num & 0xff) * SHAPE_DARKEN_FACTOR);
    return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};

interface NoticeEventDetails {
    noticeId: number;
    noticeType: string;
    personalizedNoticeSetId?: number | null;
    personalizedNoticeSetName?: string | null;
    topMembershipTargetGroupId?: number | null;
}

interface BannerEventDetails extends NoticeEventDetails {
    slideNumber: number | null;
    url: string | null;
    uiRegion: string | null;
}

const getCenterScrollLeft = (scrollPort: HTMLElement, slideIndex: number): number | null => {
    const slide = scrollPort.children[slideIndex] as HTMLElement | undefined;
    if (!slide) return null;
    return slide.offsetLeft - (scrollPort.clientWidth - slide.clientWidth) / 2;
};

const getClosestSlideIndex = (scrollPort: HTMLElement): number => {
    const portCenter = scrollPort.scrollLeft + scrollPort.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    for (let i = 0; i < scrollPort.children.length; i++) {
        const item = scrollPort.children[i] as HTMLElement;
        const itemCenter = item.offsetLeft + item.clientWidth / 2;
        const distance = Math.abs(itemCenter - portCenter);
        if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = i;
        }
    }
    return closestIndex;
};

export const ConsolidatedHeroBanner = ({
    className,
    heroBanners,
    noticeType,
}: MarketingHeroBannerProps) => {
    const {gettext} = useI18n();
    const realCount = heroBanners.length;

    // Two clones per side (not one) — the minimum buffer that keeps both
    // peek slides matching when we snap from a clone to its real twin.
    const renderedBanners = useMemo(() => {
        const reals = heroBanners.map((banner, realIndex) => ({
            banner,
            realIndex,
            isClone: false,
        }));
        if (realCount <= 1) return reals;

        const clone = (realIndex: number) => ({
            banner: heroBanners[realIndex],
            realIndex,
            isClone: true,
        });
        return [clone(realCount - 2), clone(realCount - 1), ...reals, clone(0), clone(1)];
    }, [heroBanners, realCount]);

    const totalRendered = renderedBanners.length;
    const initialRenderedIndex = realCount > 1 ? 2 : 0;

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const initializedRef = useRef(false);
    const [renderedIndex, setRenderedIndex] = useState(initialRenderedIndex);

    const getScrollPort = () =>
        wrapperRef.current?.querySelector<HTMLElement>(SCROLL_PORT_SELECTOR) ?? null;

    const scrollToSlide = (slideIndex: number, smooth: boolean) => {
        const scrollPort = getScrollPort();
        if (!scrollPort) return;
        const targetLeft = getCenterScrollLeft(scrollPort, slideIndex);
        if (targetLeft === null) return;
        scrollPort.scrollTo({left: targetLeft, behavior: smooth ? 'smooth' : 'auto'});
    };

    // The DS Carousel fires onMutation during its own mount, before this
    // effect runs. `initializedRef` gates that initial fire so the snap
    // math doesn't run against a not-yet-centered scroll port.
    useEffect(() => {
        if (realCount > 1) {
            scrollToSlide(initialRenderedIndex, false);
        }
        initializedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRenderedIndex, realCount]);

    // Map any rendered index (including clone bands) back to a real index.
    const activeRealIndex =
        realCount <= 1 ? renderedIndex : (renderedIndex - 2 + realCount) % realCount;

    // If the user settles on a clone, snap to its real twin. The 2-clone
    // buffer guarantees the snap is visually identical.
    const handleMutation = () => {
        if (!initializedRef.current) return;
        const scrollPort = getScrollPort();
        if (!scrollPort) return;
        const next = getClosestSlideIndex(scrollPort);

        if (realCount > 1) {
            const isLeadingClone = next < 2;
            const isTrailingClone = next >= totalRendered - 2;
            if (isLeadingClone || isTrailingClone) {
                const targetIndex = isLeadingClone ? next + realCount : next - realCount;
                scrollToSlide(targetIndex, false);
                setRenderedIndex(targetIndex);
                return;
            }
        }

        setRenderedIndex(next);
    };

    const handleClick = (details: BannerEventDetails) => {
        Tracker.publishEvent(new BannerClickEvent({...details}));
    };

    const getNoticeEventDetailsForSlide = (bannerData: BannerData, realIndex: number) => ({
        noticeId: bannerData.id ?? realIndex,
        noticeType,
        personalizedNoticeSetId: bannerData.personalized_notice_set_id,
        personalizedNoticeSetName: bannerData.personalized_notice_set_name,
        topMembershipTargetGroupId: bannerData.target_group_id,
        slideNumber: realIndex + 1,
    });

    const headBannerData = heroBanners[0]?.data;
    const baseNoticeEventDetails = headBannerData
        ? {
              noticeId: headBannerData.id ?? 0,
              noticeType,
              personalizedNoticeSetId: headBannerData.personalized_notice_set_id,
              personalizedNoticeSetName: headBannerData.personalized_notice_set_name,
              topMembershipTargetGroupId:
                  heroBanners[0].membership?.id ?? headBannerData.target_group_id,
          }
        : null;

    const customNextClick = () => {
        const next = renderedIndex + 1;
        if (next > totalRendered - 1) return;
        setRenderedIndex(next);
        scrollToSlide(next, true);
        if (baseNoticeEventDetails) {
            handleClick({
                ...baseNoticeEventDetails,
                uiRegion: 'next_button',
                slideNumber: null,
                url: null,
            });
        }
    };

    const customPrevClick = () => {
        const next = renderedIndex - 1;
        if (next < 0) return;
        setRenderedIndex(next);
        scrollToSlide(next, true);
        if (baseNoticeEventDetails) {
            handleClick({
                ...baseNoticeEventDetails,
                uiRegion: 'prev_button',
                slideNumber: null,
                url: null,
            });
        }
    };

    if (!heroBanners.length) return null;

    return (
        <OnePaneLayoutFullWidthSection>
            <OnePaneLayoutFullWidthSection.Content
                className={classNames(styles['consolidated-hero-banner'], className)}
            >
                <div className={styles['carousel-wrapper']} ref={wrapperRef}>
                    <Carousel
                        className={styles.carousel}
                        itemClassName={styles['carousel-item']}
                        gridMode={false}
                        showPager={false}
                        allowScroll
                        onMutation={handleMutation}
                    >
                        {renderedBanners.map(({banner, realIndex, isClone}, key) => {
                            const isActive = !isClone && key === renderedIndex;
                            const bannerData = banner.data;

                            const onCtaClick = bannerData.cta_1_url
                                ? () => {
                                      handleClick({
                                          ...getNoticeEventDetailsForSlide(bannerData, realIndex),
                                          uiRegion: 'primary_cta',
                                          url: bannerData.cta_1_url ?? '',
                                      });
                                  }
                                : undefined;

                            const trackImpression = () => {
                                if (isClone) return;
                                Tracker.publishEvent(
                                    new BannerImpressionEvent({
                                        ...getNoticeEventDetailsForSlide(bannerData, realIndex),
                                        uiRegion: null,
                                        url: null,
                                    }),
                                );
                            };

                            return (
                                <TrackImpression
                                    key={key}
                                    trackFunc={trackImpression}
                                    visibilityThreshold={0.5}
                                >
                                    <div>
                                        <ConsolidatedHeroBannerSlide
                                            data={bannerData}
                                            backgroundColor={bannerData.background_color}
                                            shapeColor={deriveShapeColor(
                                                bannerData.background_color,
                                            )}
                                            isActive={isActive}
                                            onCtaClick={onCtaClick}
                                        />
                                    </div>
                                </TrackImpression>
                            );
                        })}
                    </Carousel>
                    <div className={styles['edge-fade']} aria-hidden="true" />
                    <div
                        className={classNames(styles['edge-fade'], styles['edge-fade--right'])}
                        aria-hidden="true"
                    />
                    <button
                        type="button"
                        className={classNames(
                            styles['arrow-button'],
                            styles['arrow-button--previous'],
                        )}
                        onClick={customPrevClick}
                        aria-label={gettext('Previous slide')}
                    >
                        <Icon glyph="previous" label={false} size="large" />
                    </button>
                    <button
                        type="button"
                        className={classNames(styles['arrow-button'], styles['arrow-button--next'])}
                        onClick={customNextClick}
                        aria-label={gettext('Next slide')}
                    >
                        <Icon glyph="next" label={false} size="large" />
                    </button>
                </div>
                <div className={styles['indicator-dots-wrapper']}>
                    <IndicatorDots pageCount={realCount} activeIndex={activeRealIndex} />
                </div>
            </OnePaneLayoutFullWidthSection.Content>
        </OnePaneLayoutFullWidthSection>
    );
};
